import { defineStore } from 'pinia'
import { fetchFeatureFlags } from '@/api/featureFlags'
import { asMedicationKind } from '@/domain/medication'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { useLocationStore } from './location'
import { useStatusStore } from './status'

// Feature flags hydrated at startup from the CMS-published JSON on the public CDN
// (VITE_FEATURE_FLAGS_URL — see .env / .env.development). The load blocks first
// paint via the router guard + status store; on any fetch/validation failure we
// keep the baked-in defaults below (fail-open), so the funnel never hangs on the
// flag service.
//
// ── Changing a flag (this edits the published JSON, NOT the defaults in this file) ──
//   1. Open the CMS: https://cms.internal.appmedcloud.com  (requires Kova VPN)
//   2. Sign in with your simple.life account.
//   3. New Content → Feature Toggles.
//   4. Add/edit toggles via the visual editor, the raw JSON, or the chat-bot on the right.
//   5. Config name MUST stay "med_web_funnel" — the published filename is derived
//      from it (…/feature_flags/<name>.json) and must match VITE_FEATURE_FLAGS_URL.
//      (Renaming the config changes the URL and silently breaks the load.)
//   6. Publish → Stage + Prod.
//   7. Need the URL? kebab menu (⋮) → "copy prod url".
//
// When adding a NEW flag: also declare a typed default in state() + a getter here,
// so a missing/blocked CDN response degrades to a sane value.

// Cap on the total time hydrate() blocks first paint. axios-retry may attempt the
// fetch a few times; this deadline guarantees the splash never outlives it — whichever
// settles first wins, and a timeout simply leaves the baked-in defaults in place.
const HYDRATE_DEADLINE_MS = 4000

function withDeadline(promise, ms) {
	return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))])
}

function isHttpUrl(value) {
	try {
		const { protocol } = new URL(value)
		return protocol === 'https:' || protocol === 'http:'
	} catch {
		return false
	}
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
	state: () => ({
		// Whether hydrate() has run this page load (idempotency guard).
		hydrated: false,
		// Baked-in default used as-is if the CDN fetch fails, times out, or returns an
		// unknown value (fail-open). Overridden by the remote `defaultMedicationKind`.
		defaultMedicationKind: 'tirzepatide',
		// Провайдеры рассрочки; пустой массив — пейвол без BNPL.
		bnplProviders: ['affirm', 'klarna'],
		// AppsFlyer OneLink base for the Kova companion app. The "Download the Kova app"
		// button and the QR on the terminal screen (IntakeDone.vue) both build on it:
		// it runs through useDownload(), which layers AppsFlyer attribution params
		// onto it (UTM → pid/c/af_*, email → deep_link_value, age/sex, download_source) so
		// installs are attributed back to the funnel/campaign. This baked-in default is the
		// real OneLink; the CMS overrides it via the `onelink` key in feature_flags (fail-open).
		onelink: 'https://trykova.onelink.me/5A6x/5dny01ig',
		// Fleet-wide kill-switch for the AB-platform SDK (DEV-35179), checked by
		// src/modules/abPlatform.ts at treatment/exposure time. Fail-open by contract with
		// the platform team (mirrors the mobile integrations): only an explicit
		// `abPlatformEnabled: false` in the published JSON disables experiments — a missing
		// key or a failed CDN fetch keeps them running.
		abPlatformEnabled: true,
		// Снимать документ на экране интейка через плагин Vouched вместо загрузки файла в
		// Telegra (docs/vouched-idv.md). Живой ключ в .env уже залит, то есть на trykova.com
		// фича включена — и этот флаг остаётся ЕДИНСТВЕННЫМ рантайм-выключателем для прода:
		// `false` из CMS возвращает всех на старый аплоад без релиза, если экран поведёт себя
		// плохо. Пустой публичный ключ гасит шаг на уровне кода, но прод в этом состоянии уже
		// не находится. Default `true` — по общему для этого стора fail-open: недоступный CDN
		// флагов не должен гасить фичу молча.
		vouchedIdvEnabled: true,
		// Спрашивать ли у бека KYC-прекчек (Vouched CrossCheck) перед экраном документа
		// (DEV-35291, docs/vouched-idv.md). Прекчек делается ВСЕГДА и для всех — гейтов на
		// клиенте не осталось, все правила отказа живут у бека. Этот флаг остаётся только аварийным
		// выключателем, как `vouchedIdvEnabled` выше: `false` из CMS мгновенно и без релиза
		// перестаёт ходить в сеть. Держим его не ради раскатки (за неё отвечает защёлка бека
		// `KYC_PRECHECK_ENABLED`), а ради ОДНОГО сценария, который защёлкой бека не лечится:
		// ручка отвечает медленно, но отвечает — тогда каждый пациент упирается в ожидание до
		// PRECHECK_TIMEOUT_MS, и остановить это надо сразу, не дожидаясь деплоя бека.
		kycPrecheckEnabled: true,
		// NB: порога возраста тут НЕТ намеренно. Правило живёт только у бека
		// (`KYC_AGE_OVERRIDE_YEARS`), см. комментарий в modules/IntakeForm/kycPrecheck.ts.
	}),

	getters: {
		getDefaultMedicationKind: (state) => state.defaultMedicationKind,

		getBnplProviders() {
			if (!ENV_DEV_OR_STAGING) return this.bnplProviders
			const override = useLocationStore().getQueryParam('bnpl')
			if (typeof override === 'boolean') return override ? this.bnplProviders : []
			if (typeof override === 'string') return override.split(',').map((item) => item.trim().toLowerCase())
			return this.bnplProviders
		},

		getOnelink: (state) => state.onelink,
		getAbPlatformEnabled: (state) => state.abPlatformEnabled,
		getVouchedIdvEnabled: (state) => state.vouchedIdvEnabled,

		getKycPrecheckEnabled: (state) => state.kycPrecheckEnabled,
	},

	actions: {
		async hydrate() {
			if (this.hydrated) return
			try {
				const flags = await withDeadline(fetchFeatureFlags(), HYDRATE_DEADLINE_MS)
				const medication = asMedicationKind(flags?.defaultMedicationKind)
				if (medication) this.defaultMedicationKind = medication

				const providers = flags?.bnplProviders
				if (Array.isArray(providers)) {
					this.bnplProviders = providers.map((item) => String(item).trim().toLowerCase())
				}

				// Free-form URL — no enum to narrow against, but it must parse as an absolute
				// http(s) URL: the download screens feed it to `new URL()` inside a render-time
				// computed, and a scheme-less CMS value would throw there and blank the terminal
				// screen for every user. Anything else keeps the baked-in default (fail-open).
				const onelink = flags?.onelink
				if (typeof onelink === 'string' && isHttpUrl(onelink.trim())) {
					this.onelink = onelink.trim()
				}

				// Strictly `false` disables; any other value (missing key, typo'd string)
				// keeps the default `true` — the kill-switch must fail open.
				if (flags?.abPlatformEnabled === false) {
					this.abPlatformEnabled = false
				}

				// Только настоящий boolean: строка "false" из визуального редактора CMS иначе
				// прошла бы как truthy и оставила бы kill switch бесполезным.
				if (typeof flags?.vouchedIdvEnabled === 'boolean') {
					this.vouchedIdvEnabled = flags.vouchedIdvEnabled
				}

				if (typeof flags?.kycPrecheckEnabled === 'boolean') {
					this.kycPrecheckEnabled = flags.kycPrecheckEnabled
				}
			} finally {
				// Always flip readiness — even on failure — so the startup splash opens
				// on the baked-in defaults instead of hanging.
				this.hydrated = true
				useStatusStore().setResourceReadiness('config')
			}
		},
	},
})
